import dayjs, { Dayjs } from 'dayjs';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonProps } from './types';
import { FormikContext } from 'formik';
import { FormTrackingContext } from '../Form';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Typography from '../Typography';
import InfoButton from '../InfoButton';
import { COLORS } from '../../themev2';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { ClearIcon, MiniInfoCircleIcon } from '../../components/Icons';
import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import TextField from './TextField';
import Button from '../Button';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { styled } from '@mui/material/styles';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useUserLocation } from '../../hooks/useUserLocation';
import { Language } from '../../services/types';
import { Nullable } from '../../types/utils';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(quarterOfYear);

const SelectedDate = styled(PickersDay)`
  color: ${COLORS.brand.white};
  background-color: ${COLORS.indigo[500]};
`;
const BetweenDate = styled(PickersDay)`
  color: ${COLORS.brand.night};
  background-color: ${COLORS.indigo[100]};
`;
const HoverDate = styled(PickersDay)`
  color: ${COLORS.brand.night};
  background-color: ${COLORS.indigo[50]};
`;

export const enum RangeFieldTypes {
  NUMBER = 'number',
  DATE = 'date',
}
export const enum DateFieldTypes {
  FROM = 'fromDate',
  TO = 'toDate',
}
export const enum NumberFieldTypes {
  START = 'minValue',
  END = 'maxValue',
}
type DateValues = { [DateFieldTypes.FROM]?: Dayjs | null; [DateFieldTypes.TO]?: Dayjs | null };
type NumberValues = { [NumberFieldTypes.START]?: number | null; [NumberFieldTypes.END]?: number | null };
type NumberOrDateValues = NumberValues | DateValues;

const EMPTY_STATE: Record<RangeFieldTypes, NumberOrDateValues> = {
  [RangeFieldTypes.NUMBER]: { minValue: null, maxValue: null },
  [RangeFieldTypes.DATE]: { fromDate: null, toDate: null },
};

type RangeFieldProps = CommonProps & {
  type: RangeFieldTypes;
  onChange?: (value: NumberOrDateValues) => void;
  hasApplyButton?: boolean;
  value?: NumberOrDateValues;
  disableFuture?: boolean;
  startAdornment?: React.ReactNode;
  maxDifference?: number;
  minDate?: Dayjs;
  dateShortcuts?: DateShortcut[];
};

export type DateShortcut = {
  label: string;
  getValue: () => { fromDate: Dayjs; toDate: Dayjs } | null;
  requiredDays: number;
};

export const createDateShortcut = (
  label: string,
  getValue: () => { fromDate: Dayjs; toDate: Dayjs } | null,
  requiredDays?: number
): DateShortcut => ({
  label,
  getValue,
  requiredDays: requiredDays || 0,
});

export const DEFAULT_DATE_SHORTCUTS = {
  LAST_WEEK: createDateShortcut(
    'common:filter.dateRangePicker.lastWeek',
    () => {
      const today = dayjs();
      const prevWeek = today.subtract(7, 'day');
      return {
        fromDate: prevWeek.startOf('week').startOf('day'),
        toDate: prevWeek.endOf('week').startOf('day'),
      };
    },
    7
  ),
  LAST_SEVEN_DAYS: createDateShortcut(
    'common:filter.dateRangePicker.lastSevenDays',
    () => {
      const today = dayjs();
      return {
        fromDate: today.subtract(7, 'day').startOf('day'),
        toDate: today.startOf('day'),
      };
    },
    7
  ),
  LAST_THIRTY_DAYS: createDateShortcut(
    'common:filter.dateRangePicker.lastThirtyDays',
    () => {
      const today = dayjs();
      return {
        fromDate: today.subtract(29, 'day').startOf('day'),
        toDate: today.startOf('day'),
      };
    },
    30
  ),
  LAST_MONTH: createDateShortcut(
    'common:filter.dateRangePicker.lastMonth',
    () => {
      const today = dayjs();
      const prevMonth = today.subtract(1, 'month');
      return {
        fromDate: prevMonth.startOf('month').startOf('day'),
        toDate: prevMonth.endOf('month').startOf('day'),
      };
    },
    31
  ),
  LAST_QUARTER: createDateShortcut(
    'common:filter.dateRangePicker.lastQuarter',
    () => {
      const today = dayjs();
      const lastQuarter = today.subtract(1, 'quarter');
      return {
        fromDate: lastQuarter.startOf('quarter').startOf('day'),
        toDate: lastQuarter.endOf('quarter').startOf('day'),
      };
    },
    92 // Approximately a quarter
  ),
  LAST_YEAR: createDateShortcut(
    'common:filter.dateRangePicker.lastYear',
    () => {
      const today = dayjs();
      const prevYear = today.subtract(1, 'year');
      return {
        fromDate: prevYear.startOf('year').startOf('day'),
        toDate: prevYear.endOf('year').startOf('day'),
      };
    },
    365
  ),
  LAST_365_DAYS: createDateShortcut(
    'common:filter.dateRangePicker.lastThreeHundredSixtyFiveDays',
    () => {
      const today = dayjs();
      return {
        fromDate: today.subtract(365, 'day').startOf('day'),
        toDate: today.startOf('day'),
      };
    },
    365
  ),
};

const RangeField = ({
  type,
  error,
  name,
  onChange,
  fullWidth,
  label,
  moreInfoTooltip,
  optional,
  helperText,
  disabled,
  disableFuture,
  placeholder,
  clearable,
  startAdornment,
  sx,
  value,
  maxDifference,
  minDate,
  eventLogger,
  dateShortcuts,
}: RangeFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [allowClose, setAllowClose] = useState(true);
  const [hoverDate, setHoverDate] = useState<Nullable<Dayjs>>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { t } = useTranslation('common');
  const { language } = useUserLocation();
  const formik = useContext(FormikContext);
  const _error = error || (formik?.touched[name] && formik?.errors[name]);
  const { trackingEvent } = useContext(FormTrackingContext);

  const handleOnClose = () => {
    if (allowClose) {
      setIsOpen(false);
    }
  };

  const setFieldValue = (newValue: NumberOrDateValues | null) => {
    const updatedValue = newValue;
    formik && formik.setFieldValue(name, updatedValue || undefined);
    onChange && onChange(updatedValue || EMPTY_STATE[type]);
    trackingEvent &&
      eventLogger &&
      eventLogger(trackingEvent, name, JSON.stringify(updatedValue || EMPTY_STATE[type]));
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    const dateValue = value as DateValues;
    const fromDate = dateValue?.fromDate;
    const toDate = dateValue?.toDate;

    // Reset error state
    setHasError(false);
    setErrorMessage('');

    // If we're starting a new selection or explicitly selecting a new start date
    if (!fromDate || (fromDate && toDate) || (fromDate && newValue?.isBefore(fromDate))) {
      setFieldValue({ fromDate: newValue, toDate: null } as DateValues);
      return;
    }

    // We're selecting an end date
    if (fromDate && !toDate && newValue) {
      const daysDiff = newValue.diff(fromDate, 'day');
      if (maxDifference && daysDiff > maxDifference) {
        setHasError(true);
        setErrorMessage(t('common:filter.dateRangePicker.exceedsMaxDays', { days: maxDifference }));
        return;
      }
      setFieldValue({ fromDate: fromDate, toDate: newValue } as DateValues);
    }
  };

  const handleShortcutClick = (shortcut: DateShortcut) => {
    const result = shortcut.getValue();
    if (result) {
      const daysDiff = result.toDate.diff(result.fromDate, 'day');
      if (maxDifference && daysDiff > maxDifference) {
        setHasError(true);
        setErrorMessage(t('common:filter.dateRangePicker.exceedsMaxDays', { days: maxDifference }));
        return;
      }
      setFieldValue({ fromDate: result.fromDate, toDate: result.toDate });
      setHasError(false);
      setErrorMessage('');
    } else {
      setFieldValue(null);
      setHasError(false);
      setErrorMessage('');
    }
  };

  function CustomDay(props: any) {
    const { day, ...other } = props;
    const dateValue = value as DateValues;
    const fromDate = dateValue?.fromDate?.startOf('day');
    const toDate = dateValue?.toDate?.startOf('day');
    const date = day.startOf('day');
    let Picker: any = PickersDay;
    if (fromDate && fromDate.isSame(date)) {
      Picker = SelectedDate;
    }
    if (
      fromDate &&
      hoverDate &&
      !toDate &&
      date.isBetween(fromDate, hoverDate) &&
      fromDate.isBefore(hoverDate)
    ) {
      Picker = HoverDate;
    }
    if (fromDate && toDate && date.isBetween(fromDate, toDate)) {
      Picker = BetweenDate;
    }
    if (toDate && toDate.isSame(date)) {
      Picker = SelectedDate;
    }
    return (
      <Picker
        onMouseEnter={() => setHoverDate(date)}
        onMouseLeave={() => setHoverDate(null)}
        {...other}
        day={day}
      />
    );
  }

  const numericInputs = (
    <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '0px 12px' }}>
      <TextField
        name="min"
        label={t('common:filter.numberRangePicker.min')}
        type="number"
        value={(value as NumberValues)?.minValue?.toString() || ' '}
        onChange={(newValue) => {
          const maxValue = (value as NumberValues)?.maxValue;
          setHasError(!!maxValue && (newValue as number) > maxValue);
          setFieldValue({ minValue: newValue, maxValue: (value as NumberValues)?.maxValue });
        }}
      />
      <TextField
        name="max"
        label={t('common:filter.numberRangePicker.max')}
        type="number"
        value={(value as NumberValues)?.maxValue?.toString() || ' '}
        onChange={(newValue) => {
          const minValue = (value as NumberValues)?.minValue;
          setHasError(!!minValue && (newValue as number) < minValue);
          setFieldValue({ minValue: (value as NumberValues)?.minValue, maxValue: newValue });
        }}
        error={hasError ? t('common:filter.numberRangePicker.error') : undefined}
      />
    </Box>
  );

  const getDefaultDateShortcuts = () => {
    return Object.values(DEFAULT_DATE_SHORTCUTS);
  };

  const dateShortcutsItems = dateShortcuts || getDefaultDateShortcuts();

  const isShortcutDisabled = (shortcut: DateShortcut) => {
    if (maxDifference === undefined) return false;
    return shortcut.requiredDays > maxDifference;
  };

  const dateInputs = (
    <Box sx={{ display: 'flex' }}>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '22px', paddingLeft: '12px' }}
      >
        {dateShortcutsItems.map((shortcut) => {
          const isDisabled = isShortcutDisabled(shortcut);
          return (
            <Box key={shortcut.label}>
              <Button
                color="secondary"
                type="outline"
                size="small"
                disabled={isDisabled}
                onClick={() => handleShortcutClick(shortcut)}
                title={
                  isDisabled
                    ? t('common:filter.dateRangePicker.exceedsMaxDays', { days: maxDifference })
                    : undefined
                }
              >
                {t(shortcut.label)}
              </Button>
            </Box>
          );
        })}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '360px',
          gap: '8px',
          alignItems: 'center',
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            disableFuture={disableFuture}
            value={(value as DateValues)?.[DateFieldTypes.FROM] || null}
            onChange={handleDateChange}
            minDate={minDate}
            slots={{
              day: CustomDay,
            }}
            sx={{
              width: '320px',
              '& .MuiPickersDay-root:hover': {
                color: COLORS.brand.night,
                backgroundColor: COLORS.brand.white,
                border: `1px solid ${COLORS.indigo[500]}`,
              },
              '& .Mui-selected': {
                color: COLORS.brand.white,
                backgroundColor: COLORS.indigo[500],
              },
              '& .Mui-selected:hover': {
                color: COLORS.brand.night,
                backgroundColor: COLORS.brand.white,
                border: `1px solid ${COLORS.indigo[500]}`,
              },
              '& .Mui-selected:focus': {
                color: COLORS.brand.white,
                backgroundColor: `${COLORS.indigo[500]} !important`,
              },
            }}
          />
        </LocalizationProvider>
        {hasError && (
          <Box
            sx={{
              backgroundColor: COLORS.orange[100],
              padding: '8px 12px',
              borderRadius: '4px',
              width: '260px',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: COLORS.orange[900],
                display: 'flex',
                alignItems: 'flex-start',
                gap: '4px',
                fontSize: '12px',
                lineHeight: '16px',
              }}
            >
              <span style={{ flexShrink: 0 }}>
                <MiniInfoCircleIcon color={COLORS.orange[900]} />
              </span>
              <span style={{ color: COLORS.brand.night }}>{errorMessage}</span>
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  const getSelectText = () => {
    let text = placeholder;
    if (type === RangeFieldTypes.NUMBER) {
      const values = value as NumberValues;
      if (values?.minValue && values?.maxValue) {
        text = `${values?.minValue} - ${values?.maxValue}`;
      } else if (values?.minValue) {
        text = `${values?.minValue} ${t('common:filter.rangePicker.orMore')}`;
      } else if (values?.maxValue) {
        text = `${values?.maxValue} ${t('common:filter.rangePicker.orLess')}`;
      }
    } else {
      const values = value as DateValues;
      const format = language === Language.EN ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
      const from = values?.fromDate?.format(format);
      const to = values?.toDate?.format(format);
      if (values?.fromDate && values?.toDate) {
        text = `${from} - ${to}`;
      } else if (values?.fromDate) {
        text = `${from} - ${format}`;
      } else if (values?.toDate) {
        text = `${format} - ${to}`;
      }
    }
    return text;
  };

  return (
    <FormControl fullWidth={fullWidth} error={!!_error}>
      {label && (
        <FormLabel sx={{ padding: '8px 0', fontWeight: 600, display: 'flex' }}>
          <Typography>{label}</Typography>
          {moreInfoTooltip && <InfoButton title={moreInfoTooltip} />}
          {optional && (
            <Typography sx={{ color: COLORS.gray[1000], marginLeft: 'auto' }}>{t('optional')}</Typography>
          )}
        </FormLabel>
      )}
      <Select
        color="secondary"
        fullWidth
        startAdornment={
          startAdornment ? (
            <InputAdornment
              position="start"
              style={{
                width: 'auto',
                color: '#3B4567',
              }}
            >
              {startAdornment}
            </InputAdornment>
          ) : null
        }
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={handleOnClose}
        disabled={disabled}
        displayEmpty
        variant="outlined"
        renderValue={() => {
          return <Typography style={{ color: COLORS.brand.night }}>{getSelectText()}</Typography>;
        }}
        sx={{
          background: disabled ? COLORS.gray[300] : COLORS.brand.white,
          paddingRight: '8px',
          '& .MuiSelect-select': {
            paddingY: '12px',
            paddingRight: '0px !important',
          },
          '& fieldset': {
            borderColor: COLORS.gray[900],
            borderRadius: '8px',
          },
          ...sx,
        }}
        endAdornment={
          clearable && formik?.values[name] ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="clear selection"
                onClick={() => setFieldValue(null)}
                edge="end"
                size="small"
                sx={{ marginRight: '1em', color: '#4F5A84' }}
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ) : null
        }
      >
        {
          <Box
            onMouseEnter={() => setAllowClose(false)}
            onMouseLeave={() => setAllowClose(true)}
            sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 4px' }}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Typography variant="body2" sx={{ padding: '0px 12px', fontSize: '14px', fontWeight: 600 }}>
              {t('common:filter.dateRangePicker.selectRange')}
            </Typography>
            <Divider sx={{ margin: '0px 12px' }} />
            {type === RangeFieldTypes.NUMBER ? numericInputs : dateInputs}
            <Divider sx={{ margin: '0px 12px' }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1.5em',
                alignItems: 'center',
                padding: '0px 12px',
              }}
            >
              <Button
                type="link"
                color="tertiary"
                onClick={() => {
                  setFieldValue(null);
                  setHasError(false);
                  setErrorMessage('');
                }}
              >
                {t('common:filter.dateRangePicker.reset')}
              </Button>
              <Button
                type="outline"
                disabled={hasError}
                onClick={() => setIsOpen(false)}
                isFullHeight={false}
                color="secondary"
              >
                {t('common:filter.dateRangePicker.apply')}
              </Button>
            </Box>
          </Box>
        }
      </Select>
      {(!!_error || helperText) && (
        <FormHelperText sx={{ color: 'inherit', whiteSpace: 'pre-line', fontSize: '14px' }}>
          {_error || helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default RangeField;
